<script setup>
    import { ref, watch, computed } from 'vue';
    import { Cropper, CircleStencil } from 'vue-advanced-cropper';
    import 'vue-advanced-cropper/dist/style.css';
    import Modal from './Modal.vue';
    import TextInput from '@/components/forms/inputs/text-input.vue';
    import { useToast } from '@/composables/use-toast';
    import { getEvalLLMById, createEvalLLM, updateEvalLLM, deleteEvalLLM } from '@/services/evalLLMService';
    import { getAgents } from '@/services/agentsService';
    import { fetchListings } from '@/services/api';
    import CheckboxList from '@/components/forms/inputs/checkbox-list.vue';
    import Alert from '@/components/Alert.vue';
    
    const props = defineProps({
        isOpen: {
            type: Boolean,
            default: false
        },
        llmId: {
            type: [String, Number],
            default: null
        }
    });
    
    const emit = defineEmits(['close', 'saved', 'delete']);
    
    const { addToast } = useToast();
    
    const formData = ref({
        name: '',
        hostname: '',
        active: true,
        model_config: '',
        backfill: [],
        logo: null,
        hidden: false,
        self_serve_enabled: false,
        prompt_price_per_1m: null,
        completion_price_per_1m: null,
        agent_ids: []
    });

    const pricesSource = ref(null);
    const pricesSyncedAt = ref(null);
    
    const showDeleteAlert = ref(false);
    const isLoading = ref(false);
    const isSaving = ref(false);
    const isDeleting = ref(false);
    const isLoadingListings = ref(false);
    const isLoadingAgents = ref(false);
    const agentsOptions = ref([]);
    const jsonValidationError = ref('');
    const isValidJson = ref(true);
    const modelConfigFileInput = ref(null);
    const logoFileInput = ref(null);
    const cropperRef = ref(null);
    
    const logoPreview = ref(null);
    const logoBase64 = ref(null);
    
    const listings = ref([]);
    const listingsOptions = ref([]);
    const backfillData = ref([]);
    const showCropper = ref(false);
    const imageSource = ref('');
    const cropperResult = ref(null);
    const hiddenInput = ref(false);
    
    const isEditing = computed(() => !!props.llmId);
    
    const resetForm = () => {
        formData.value = {
            name: '',
            hostname: '',
            active: true,
            model_config: '',
            backfill: [],
            logo: null,
            hidden: false,
            self_serve_enabled: false,
            prompt_price_per_1m: null,
            completion_price_per_1m: null,
            agent_ids: []
        };
        pricesSource.value = null;
        pricesSyncedAt.value = null;
        backfillData.value = [];
        jsonValidationError.value = '';
        logoPreview.value = null;
        logoBase64.value = null;
        imageSource.value = '';
        cropperResult.value = null;
        showCropper.value = false;
    };
    
    const loadEvalLLM = async () => {
        if (!props.llmId) {
            resetForm();
            return;
        }
        
        isLoading.value = true;
        
        try {
            const response = await getEvalLLMById(props.llmId);
            formData.value.name = response.data.llm.name;
            formData.value.hostname = response.data.llm.hostname;
            formData.value.active = Boolean(response.data.llm.active);
            formData.value.hidden = Boolean(response.data.llm?.hidden ?? false);
            formData.value.self_serve_enabled = Boolean(response.data.llm?.self_serve_enabled ?? false);
            formData.value.prompt_price_per_1m =
                response.data.llm?.prompt_price_per_1m != null
                    ? Number(response.data.llm.prompt_price_per_1m)
                    : null;
            formData.value.completion_price_per_1m =
                response.data.llm?.completion_price_per_1m != null
                    ? Number(response.data.llm.completion_price_per_1m)
                    : null;
            pricesSource.value = response.data.llm?.prices_source ?? null;
            pricesSyncedAt.value = response.data.llm?.prices_synced_at ?? null;
            hiddenInput.value = formData.value.hidden;
            if (response.data.llm.model_config) {
                try {
                    const parsed = typeof response.data.llm.model_config === 'string' 
                        ? JSON.parse(response.data.llm.model_config)
                        : response.data.llm.model_config;
                    formData.value.model_config = JSON.stringify(parsed, null, 2);
                    jsonValidationError.value = '';
                } catch (e) {
                    formData.value.model_config = response.data.llm.model_config;
                    validateJson(response.data.llm.model_config);
                }
            } else {
                formData.value.model_config = '';
            }
    
            if (response.data.llm.backfill && response.data.llm.backfill.length > 0) {
                backfillData.value = response.data.llm.backfill;
                formData.value.backfill = response.data.llm.backfill.map(backfill => String(backfill.dataset_id));
                updateListingsOptionsWithStatus();
            } else {
                backfillData.value = [];
                formData.value.backfill = [];
            }

            formData.value.agent_ids = (response.data.llm.agents || []).map((a) => a.id);
    
            if (response.data.llm.icon) {
                logoPreview.value = response.data.llm.icon;
            } else {
                logoPreview.value = null;
            }
        } catch (error) {
            addToast(error.response?.data?.message, 'error');
        } finally {
            isLoading.value = false;
        }
    };
    
    const validateJson = (jsonString) => {
        if (!jsonString || !jsonString.trim()) {
            jsonValidationError.value = '';
            isValidJson.value = true;
            return {
                valid: isValidJson.value,
                error: jsonValidationError.value
            };
        }
        
        try {
            if (typeof jsonString !== 'string') {
                jsonValidationError.value = 'Please upload a valid JSON string';
                isValidJson.value = false;
                return {
                    valid: isValidJson.value,
                    error: jsonValidationError.value
                };
            }
            JSON.parse(jsonString);
            jsonValidationError.value = '';
            isValidJson.value = true;
    
            return {
                valid: isValidJson.value,
                error: jsonValidationError.value
            };
        } catch (e) {
            jsonValidationError.value = 'Please upload a valid JSON file: ' + e.message;
            isValidJson.value = false;
            return {
                valid: isValidJson.value,
                error: jsonValidationError.value
            };
        }
    };
    
    const handleJsonInput = (value) => {
        formData.value.model_config = value;
        validateJson(value);
    };
    
    const handleFileSelect = async (event) => {
        const file = event.target.files[0];
        if (!file) return;
        if (!file.name.endsWith('.json')) {
            addToast('Please upload a JSON file', 'error');
            event.target.value = '';
            return;
        }
        try {
            const text = await file.text();
            const validation = validateJson(text);
            if (!validation.valid) {
                event.target.value = '';
                throw new Error(validation.error);
            }
            formData.value.model_config = text;
        } catch (error) {
            console.error(error);
            addToast(error.message, 'error');
            event.target.value = '';
            return;
        }
    };
    
    const handleLogoSelect = (event) => {
        const file = event.target.files[0];
        if (!file) return;
    
        const validTypes = ['image/png', 'image/jpeg', 'image/jpg', 'image/webp'];
        if (!validTypes.includes(file.type)) {
            addToast('Please upload a PNG, JPG or WEBP file', 'error');
            event.target.value = '';
            return;
        }
    
        const maxSize = 5 * 1024 * 1024;
        if (file.size > maxSize) {
            addToast('Please upload a file less than 5MB', 'error');
            event.target.value = '';
            return;
        }
    
        const reader = new FileReader();
        reader.onload = (e) => {
            imageSource.value = e.target.result;
            showCropper.value = true;
        };
        reader.readAsDataURL(file);
    };
    
    const removeLogo = () => {
        logoPreview.value = null;
        logoBase64.value = null;
        formData.value.logo = null;
        imageSource.value = '';
        cropperResult.value = null;
        showCropper.value = false;
        if (logoFileInput.value) {
            logoFileInput.value.value = '';
        }
    };
    
    const handleLogoDrop = (event) => {
        const file = event.dataTransfer.files[0];
        if (!file) return;
    
        const validTypes = ['image/png', 'image/jpeg', 'image/jpg', 'image/webp'];
        if (!validTypes.includes(file.type)) {
            addToast('Please upload a PNG, JPG or WEBP file', 'error');
            return;
        }
    
        const maxSize = 5 * 1024 * 1024;
        if (file.size > maxSize) {
            addToast('Please upload a file less than 5MB', 'error');
            return;
        }
    
        const reader = new FileReader();
        reader.onload = (e) => {
            imageSource.value = e.target.result;
            showCropper.value = true;
        };
        reader.readAsDataURL(file);
    };
    
    const onCropperChange = ({ canvas }) => {
        cropperResult.value = canvas;
    };
    
    const saveCroppedImage = () => {
        if (!cropperResult.value) return;
    
        const targetSize = 256;
        const canvas = document.createElement('canvas');
        canvas.width = targetSize;
        canvas.height = targetSize;
        const ctx = canvas.getContext('2d');
    
        ctx.drawImage(cropperResult.value, 0, 0, targetSize, targetSize);
    
        const base64Image = canvas.toDataURL('image/webp', 0.9);
    
        logoPreview.value = base64Image;
        logoBase64.value = base64Image;
        
        showCropper.value = false;
    };
    
    const cancelCropping = () => {
        showCropper.value = false;
        imageSource.value = '';
        cropperResult.value = null;
        if (logoFileInput.value) {
            logoFileInput.value.value = '';
        }
    };
    
    const validateFormData = () => {
        if (!formData.value.name.trim()) {
            addToast('Name is required', 'error');
            return false;
        }
    
        if (!formData.value.hostname.trim()) {
            addToast('Hostname is required', 'error');
            return false;
        }
    
        if (formData.value.model_config && formData.value.model_config.trim()) {
            const validation = validateJson(formData.value.model_config);
            if (!validation.valid) {
                addToast('Please fix the JSON validation errors before saving', 'error');
                return false;
            }
        }
    
        return true;
    };
    
    const buildPayload = () => {
        const payload = {
            name: formData.value.name,
            hostname: formData.value.hostname,
            active: formData.value.active,
            model_config: null,
            backfill: null,
            hidden: hiddenInput.value,
            self_serve_enabled: Boolean(formData.value.self_serve_enabled),
            prompt_price_per_1m:
                formData.value.prompt_price_per_1m === '' || formData.value.prompt_price_per_1m == null
                    ? null
                    : Number(formData.value.prompt_price_per_1m),
            completion_price_per_1m:
                formData.value.completion_price_per_1m === '' || formData.value.completion_price_per_1m == null
                    ? null
                    : Number(formData.value.completion_price_per_1m),
            agent_ids: Array.isArray(formData.value.agent_ids)
                ? formData.value.agent_ids.map(Number).filter((id) => !Number.isNaN(id))
                : []
        };
    
        if (formData.value.model_config && formData.value.model_config.trim()) {
            const parsed = JSON.parse(formData.value.model_config);
            payload.model_config = JSON.stringify(parsed);
        }
    
        if (!isEditing.value && formData.value.backfill && formData.value.backfill.length > 0) {
            payload.backfill = formData.value.backfill.map(dataset_id => ({
                dataset_id: dataset_id,
                status: 'pending'
            }));
        }
    
        if (logoBase64.value) {
            payload.icon = logoBase64.value; 
        }
    
        return payload;
    };
    
    const handleSave = async () => {
        if (!validateFormData()) {
            return;
        }
    
        isSaving.value = true;
    
        try {
            const payload = buildPayload();
            
            const response = props.llmId
                ? await updateEvalLLM(props.llmId, payload)
                : await createEvalLLM(payload);
            
            addToast(response.data.message, 'success');
            
            emit('saved');
            emit('close');
        } catch (error) {
            addToast(error.response?.data?.message, 'error');
            console.error(error);
        } finally {
            isSaving.value = false;
        }
    };
    
    const handleDelete = async () => {
        if (!props.llmId) {
            return;
        }
    
        isDeleting.value = true;
    
        try {
            const response = await deleteEvalLLM(props.llmId);
            addToast(response.data.message, 'success');
            emit('delete');
            emit('close');
        }
        catch (error) {
            addToast(error.response?.data?.message, 'error');
            console.error(error);
        }
        finally {
            isDeleting.value = false;
            showDeleteAlert.value = false;
        }
    };
    
    const handleDeleteConfirm = () => {
        handleDelete();
    };
    
    const handleCloseDeleteAlert = () => {
        isDeleting.value = false;
        showDeleteAlert.value = false;
    };
    
    const loadListings = async () => {
        isLoadingListings.value = true;
        try {
            const rawListings = await fetchListings(0, 1000, true);
            if (!rawListings || rawListings.length === 0) {
                listingsOptions.value = [];
                return;
            }
            
            listings.value = rawListings;
            
            const validOptions = [];
            
            rawListings.forEach((listing) => {
                if (!listing.datasets || !Array.isArray(listing.datasets) || listing.datasets.length === 0) {
                    addToast(`The listing "${listing.name || 'Unnamed Listing'}" has no datasets. Please add at least one dataset to the listing.`, 'warning');
                    validOptions.push({
                        label: `${listing.name || 'Unnamed Listing'} (No datasets)`,
                        value: `disabled-${listing.id || listing.name || 'unknown'}`,
                        disabled: true
                    });
                    return;
                }
                
                const dataset = listing.datasets[0];
                const datasetId = dataset?.id || dataset?.dataset_id || (dataset?.dataset?.id);
                
                if (!datasetId) {
                    addToast(`The Listing ${listing.name || 'Unnamed Listing'} has no dataset ID. Please add a dataset to the listing.`, 'warning');
                    validOptions.push({
                        label: `${listing.name || 'Unnamed Listing'} (No dataset ID)`,
                        value: `disabled-${listing.id || listing.name || 'unknown'}`,
                        disabled: true
                    });
                    return;
                }
                
                validOptions.push({
                    label: listing.name || 'Unnamed Listing',
                    value: String(datasetId),
                });
            });
            
            listingsOptions.value = validOptions;
            updateListingsOptionsWithStatus();
        } catch (error) {
            console.error('Error loading listings:', error);
            addToast('Failed to load listings', 'error');
            listingsOptions.value = [];
        } finally {
            isLoadingListings.value = false;
        }
    };
    
    const updateListingsOptionsWithStatus = () => {
        if (!backfillData.value || backfillData.value.length === 0) {
            return;
        }
        
        const statusMap = new Map();
        backfillData.value.forEach(backfill => {
            statusMap.set(String(backfill.dataset_id), backfill.status);
        });
        
        listingsOptions.value = listingsOptions.value.map(option => {
            const status = statusMap.get(option.value);
            return {
                ...option,
                status: status || undefined
            };
        });
    };
    
    const handleClose = () => {
        if (!isSaving.value) {
            resetForm();
            emit('close');
        }
    };

    const handleToggleHidden = () => {
        hiddenInput.value = !hiddenInput.value;
    };
    
    
    const loadAgents = async () => {
        isLoadingAgents.value = true;
        try {
            const response = await getAgents();
            const agents = response.data?.agents || [];
            agentsOptions.value = agents.map((agent) => ({
                label: agent.name || `Agent #${agent.id}`,
                value: agent.id
            }));
        } catch (error) {
            addToast(error.response?.data?.message || 'Failed to load agents', 'error');
            agentsOptions.value = [];
        } finally {
            isLoadingAgents.value = false;
        }
    };

    watch(() => props.isOpen, (newValue) => {
        if (newValue) {
            loadListings();
            loadEvalLLM();
        }
    });
    
    watch(() => props.llmId, () => {
        if (props.isOpen) {
            loadEvalLLM();
        }
    });
    </script>

<template>
    <Modal 
        :is-open="isOpen" 
        :title="llmId ? 'Edit Eval LLM' : 'Create Eval LLM'"
        size="3xl"
        :close-on-click-outside="false"
        @close="handleClose"
    >
        <!-- Loading State -->
        <div v-if="isLoading" class="flex justify-center items-center py-16">
            <span class="sr-only">Loading...</span>
            <div class="animate-spin rounded-full h-12 w-12 border-b-2 border-echoes-primary"></div>
        </div>

        <!-- Form Content -->
        <form v-else @submit.prevent="handleSave" class="space-y-6">
            <!-- Description -->
            <p class="text-sm text-white/60">
                {{ llmId ? 'Update the information for this Eval LLM.' : 'Fill in the fields below to create a new Eval LLM.' }}
            </p>

            <!-- Hostname and Name Fields -->
            <div class="grid grid-cols-1 md:grid-cols-2 gap-6">
                <!-- Hostname Field -->
                <div class="space-y-2">
                    <label for="llm-hostname" class="block text-sm font-semibold text-white">
                        Hostname <span class="text-red-500">*</span>
                    </label>
                    <TextInput
                        id="llm-hostname"
                        v-model="formData.hostname"
                        type="text"
                        required
                        placeholder="openai, anthropic, google, etc."
                        :disabled="isSaving"
                        bg-class="bg-echoes-background-black"
                        border-class="border-echoes-border-light"
                        hover-border-class="border-white/20"
                        focus-border-class="border-echoes-primary"
                        dataTestId="eval-llm-hostname-input"
                    />
                    <p class="text-xs text-white/50">
                        Provide the hostname.
                    </p>
                </div>

                <!-- Name Field -->
                <div class="space-y-2">
                    <label for="llm-name" class="block text-sm font-semibold text-white">
                        LLM Name <span class="text-red-500">*</span>
                    </label>
                    <TextInput
                        id="llm-name"
                        v-model="formData.name"
                        type="text"
                        required
                        placeholder="GPT-4, Claude, Llama 2"
                        :disabled="isSaving"
                        bg-class="bg-echoes-background-black"
                        border-class="border-echoes-border-light"
                        hover-border-class="border-white/20"
                        focus-border-class="border-echoes-primary"
                        dataTestId="eval-llm-name-input"
                    />
                    <p class="text-xs text-white/50">
                        Provide a name for this Eval LLM.
                    </p>
                </div>
            </div>

            <!-- Active Field with Toggle -->
            <div class="space-y-2">
                <div class="block text-sm font-semibold text-white">
                    Status
                </div>
                <div class="flex items-center gap-3">
                    <!-- Toggle Switch -->
                    <button
                        type="button"
                        @click="formData.active = !formData.active"
                        :disabled="isSaving"
                        :class="[
                            'relative inline-flex h-6 w-12 flex-shrink-0 cursor-pointer rounded-full border-2 border-transparent transition-colors duration-200 ease-in-out disabled:opacity-50 disabled:cursor-not-allowed',
                            formData.active ? 'bg-green-600' : 'bg-gray-600'
                        ]"
                        :title="formData.active ? 'Click to deactivate' : 'Click to activate'"
                        :aria-label="`Toggle LLM status. Currently ${formData.active ? 'active' : 'inactive'}`"
                        data-testid="eval-llm-active-toggle"
                    >
                        <span
                            aria-hidden="true"
                            :class="[
                                'pointer-events-none inline-block h-5 w-5 transform rounded-full bg-white shadow-lg ring-0 transition duration-200 ease-in-out',
                                formData.active ? 'translate-x-6' : 'translate-x-0'
                            ]"
                        ></span>
                    </button>
                    
                    <!-- Status Label -->
                    <span 
                        :class="[
                            'text-sm font-medium',
                            formData.active ? 'text-green-400' : 'text-gray-400'
                        ]"
                    >
                        {{ formData.active ? 'Active' : 'Inactive' }}
                    </span>
                </div>
                <p class="text-xs text-white/50">
                    {{ formData.active ? 'This LLM is active and available for use.' : 'This LLM is inactive and cannot be used.' }}
                </p>
            </div>

            <div class="space-y-2">
                <div class="block text-sm font-semibold text-white">
                    Self-serve catalog
                </div>
                <div class="flex items-center gap-3">
                    <button
                        type="button"
                        @click="formData.self_serve_enabled = !formData.self_serve_enabled"
                        :disabled="isSaving"
                        :class="[
                            'relative inline-flex h-6 w-12 flex-shrink-0 cursor-pointer rounded-full border-2 border-transparent transition-colors duration-200 ease-in-out disabled:opacity-50 disabled:cursor-not-allowed',
                            formData.self_serve_enabled ? 'bg-green-600' : 'bg-gray-600'
                        ]"
                        :title="formData.self_serve_enabled ? 'Disable self-serve' : 'Enable self-serve'"
                        :aria-label="`Toggle self-serve. Currently ${formData.self_serve_enabled ? 'enabled' : 'disabled'}`"
                        data-testid="eval-llm-self-serve-toggle"
                    >
                        <span
                            aria-hidden="true"
                            :class="[
                                'pointer-events-none inline-block h-5 w-5 transform rounded-full bg-white shadow-lg ring-0 transition duration-200 ease-in-out',
                                formData.self_serve_enabled ? 'translate-x-6' : 'translate-x-0'
                            ]"
                        ></span>
                    </button>
                    <span
                        :class="[
                            'text-sm font-medium',
                            formData.self_serve_enabled ? 'text-green-400' : 'text-gray-400'
                        ]"
                    >
                        {{ formData.self_serve_enabled ? 'Self-serve enabled' : 'Self-serve disabled' }}
                    </span>
                </div>
                <p class="text-xs text-white/50">
                    When enabled, this model appears in the public catalog for user model-runs (Benchmark credits).
                </p>
                <div class="grid grid-cols-1 md:grid-cols-2 gap-3 pt-2">
                    <div class="space-y-1">
                        <label for="llm-prompt-price" class="block text-sm font-semibold text-white">
                            Prompt price (USD / 1M tokens)
                        </label>
                        <input
                            id="llm-prompt-price"
                            type="number"
                            min="0"
                            step="any"
                            :disabled="isSaving"
                            v-model="formData.prompt_price_per_1m"
                            class="w-full rounded-md border border-white/20 bg-white/5 px-3 py-2 text-sm text-white placeholder:text-white/40 focus:outline-none focus:ring-2 focus:ring-green-500 disabled:opacity-50"
                            placeholder="e.g. 1.5"
                            data-testid="eval-llm-prompt-price"
                        />
                    </div>
                    <div class="space-y-1">
                        <label for="llm-completion-price" class="block text-sm font-semibold text-white">
                            Completion price (USD / 1M tokens)
                        </label>
                        <input
                            id="llm-completion-price"
                            type="number"
                            min="0"
                            step="any"
                            :disabled="isSaving"
                            v-model="formData.completion_price_per_1m"
                            class="w-full rounded-md border border-white/20 bg-white/5 px-3 py-2 text-sm text-white placeholder:text-white/40 focus:outline-none focus:ring-2 focus:ring-green-500 disabled:opacity-50"
                            placeholder="e.g. 2.0"
                            data-testid="eval-llm-completion-price"
                        />
                    </div>
                </div>
                <p class="text-xs text-white/50" data-testid="eval-llm-price-origin">
                    <template v-if="pricesSource || pricesSyncedAt">
                        Price origin:
                        {{ pricesSource === 'openrouter' ? 'OpenRouter sync' : pricesSource === 'manual' ? 'Manual' : 'Unknown' }}
                        <template v-if="pricesSyncedAt">
                            · last set {{ new Date(pricesSyncedAt).toLocaleString() }}
                        </template>
                    </template>
                    <template v-else>
                        No price origin recorded yet (set manually or wait for OpenRouter sync).
                    </template>
                </p>
            </div>
            <!-- Model Config Field -->
                <div class="space-y-2">
                    <label for="llm-model-config" class="block text-sm font-semibold text-white flex items-center gap-2">
                        Model Config <span class="text-white/50">(You can upload a JSON file or paste the JSON directly)</span>
                    </label>
                    <input
                        ref="modelConfigFileInput"
                        type="file"
                        @change="handleFileSelect"
                        class="hidden"
                        accept=".json"
                        data-testid="eval-llm-model-config-file-input"
                    />
                    <button
                        type="button"
                        @click="modelConfigFileInput?.click()"
                        class="inline-flex items-center justify-center px-4 py-1.5 text-sm 2xl:text-base font-medium rounded-lg bg-echoes-background-black/30 text-white hover:bg-white/10 transition-colors duration-200 border border-echoes-border-light disabled:opacity-50 disabled:cursor-not-allowed"
                        data-testid="eval-llm-upload-json-button"
                    >
                        <i class="mdi mdi-upload mr-2 text-base 2xl:text-lg"></i>
                        Upload JSON File
                    </button>
                    <div class="relative model-config-wrapper">
                        <TextInput
                            id="llm-model-config"
                            :model-value="formData.model_config"
                            key="model-config-input"
                            type="text"
                            :is-textarea="true"
                            placeholder='{"model": "openai/gpt-5", "temperature": 0.2, "top_p": 0.8, ...}'
                            :disabled="isSaving"
                            bg-class="bg-echoes-background-black"
                            border-class="border-echoes-border-light"
                            hover-border-class="border-white/20"
                            focus-border-class="border-echoes-primary"
                            @update:model-value="handleJsonInput"
                            class="font-mono text-sm"
                            dataTestId="eval-llm-model-config-input"
                        />
                    </div>
                    <div v-if="jsonValidationError" class="text-xs text-red-400 mt-1">
                        {{ jsonValidationError }}
                    </div>
                    <p class="text-xs text-white/50">
                        Optional: JSON configuration parameters for model calls (e.g., temperature, top_p, max_tokens, etc.)
                    </p>
                </div>

                 <!-- Logo Upload Field -->
            <div class="space-y-2">
                <label for="eval-llm-logo-file-input" class="block text-sm font-semibold text-white">
                    Image Upload
                </label>
                <input
                    ref="logoFileInput"
                    type="file"
                    @change="handleLogoSelect"
                    class="hidden"
                    accept="image/png,image/jpeg,image/jpg,image/webp"
                    data-testid="eval-llm-logo-file-input"
                />
                <div
                    @click="logoFileInput?.click()"
                    @dragover.prevent
                    @drop.prevent="handleLogoDrop"
                    class="relative border-2 border-dashed border-gray-400/50 rounded-lg bg-black cursor-pointer hover:border-echoes-primary/50 transition-colors duration-200"
                    :class="{ 'border-echoes-primary': logoPreview }"
                    data-testid="eval-llm-logo-upload-area"
                >
                    <div v-if="!logoPreview" class="flex flex-col items-center justify-center py-8 px-4">
                        <div class="mb-4 bg-echoes-primary/20 rounded-lg p-2">
                            <svg class="w-8 h-8 text-echoes-primary" xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-linecap="round" stroke-linejoin="round" stroke-width="2">
                                <path d="M4 15v2a3 3 0 0 0 3 3h10a3 3 0 0 0 3-3v-2M12 4v12m0-12l4 4m-4-4L8 8"/>
                            </svg>
                        </div>
                        <p class="text-white text-sm font-semibold mb-1">Click to upload</p>
                        <p class="text-white/60 text-xs">PNG, JPG, WEBP up to 5MB</p>
                    </div>
                    <div v-else class="relative py-6 px-6">
                        <div class="flex flex-col items-center justify-center">
                            <div class="logo-preview-circle">
                                <img :src="logoPreview" alt="Logo preview" class="logo-preview-image" />
                            </div>
                            <button
                                type="button"
                                @click.stop="removeLogo"
                                class="mt-4 inline-flex items-center justify-center px-4 py-2 bg-red-500 hover:bg-red-600 text-white text-sm font-medium rounded-lg transition-colors duration-200 shadow-md"
                                :disabled="isSaving"
                                data-testid="eval-llm-remove-logo-button"
                            >
                                <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" class="w-4 h-4 mr-2">
                                    <path fill="currentColor"
                                        d="M20,6H16V5a3,3,0,0,0-3-3H11A3,3,0,0,0,8,5V6H4A1,1,0,0,0,4,8H5V19a3,3,0,0,0,3,3h8a3,3,0,0,0,3-3V8h1a1,1,0,0,0,0-2ZM10,5a1,1,0,0,1,1-1h2a1,1,0,0,1,1,1V6H10Zm7,14a1,1,0,0,1-1,1H8a1,1,0,0,1-1-1V8H17Z">
                                    </path>
                                </svg>
                                Remove Image
                            </button>
                        </div>
                    </div>
                </div>
            </div>

            <!-- Backfill Field -->
            <div class="space-y-2">
                <label for="llm-backfill" class="block text-sm font-semibold text-white flex items-center gap-2">
                    Backfill <span class="text-white/50">(Select the listings to backfill for this LLM)</span>
                </label>
                <div class="backfill-wrapper">
                    <CheckboxList 
                        v-model="formData.backfill"
                        :options="listingsOptions"
                        search-placeholder="Search listings..."
                        :disabled="isSaving || isEditing"
                        :loading="isLoadingListings"
                        max-height="300px"
                        dataTestId="eval-llm-backfill-input"
                    />
                </div>
                <p class="text-xs text-white/50">
                    Optional: Select the listings to backfill for this LLM. <span v-if="isEditing" class="text-yellow-400">(Only available when creating a new LLM)</span>
                </p>
            </div>

            <!-- Divider -->
            <div class="border-t border-echoes-border-light"></div>

            <!-- Action Buttons -->
            <div class="flex items-center w-full justify-between">
                <div class="flex items-center gap-2">
                    <button
                        v-if="isEditing"
                        type="button"
                        @click="showDeleteAlert = true"
                        :disabled="isDeleting || isSaving"
                        class="inline-flex items-center justify-center px-4 py-1.5 text-sm 2xl:text-base font-medium rounded-lg bg-echoes-background-black/30 text-white hover:bg-white/10 transition-colors duration-200 border border-echoes-border-light disabled:opacity-50 disabled:cursor-not-allowed"
                        data-testid="eval-llm-delete-button"
                    >
                        <i class="mdi mdi-delete mr-2 text-base 2xl:text-lg text-red-500"></i>
                        Delete
                    </button>
                    <div v-if="isEditing" class="flex items-center gap-1 cursor-pointer transition-colors duration-200" :class="hiddenInput ? 'text-white/50' : 'text-white hover:text-white/80'" @click="handleToggleHidden" :disabled="isSaving">
                        <i :class="hiddenInput ? 'mdi mdi-eye-off text-white/50' : 'mdi mdi-eye text-white'" class="text-base 2xl:text-lg"></i>
                        <span class="text-sm text-white/50">{{ hiddenInput ? 'Hidden' : 'Visible' }}</span>
                    </div>
                </div>
                <div class="flex items-center gap-4">
                    <button
                        type="button"
                        @click="handleClose"
                        :disabled="isSaving"
                        class="inline-flex items-center justify-center px-4 py-1.5 text-sm 2xl:text-base font-medium rounded-lg bg-echoes-background-black/30 text-white hover:bg-white/10 transition-colors duration-200 border border-echoes-border-light disabled:opacity-50 disabled:cursor-not-allowed"
                        data-testid="eval-llm-cancel-button"
                    >
                        <i class="mdi mdi-close mr-2 text-base 2xl:text-lg"></i>
                        Cancel
                    </button>
                    <button
                        type="submit"
                        :disabled="isSaving || !formData.name.trim() || !formData.hostname.trim() || !!jsonValidationError"
                        class="inline-flex items-center justify-center px-4 py-1.5 text-sm 2xl:text-base font-semibold rounded-lg bg-echoes-primary text-white hover:bg-echoes-primary/90 transition-colors duration-200 disabled:opacity-50 disabled:cursor-not-allowed shadow-lg"
                        data-testid="eval-llm-save-button"
                    >
                        <i v-if="!isSaving" class="mdi mdi-content-save mr-2 text-base 2xl:text-lg"></i>
                        <i v-else class="mdi mdi-loading mdi-spin mr-2 text-base 2xl:text-lg"></i>
                        {{ isSaving ? 'Saving...' : 'Save' }}
                    </button>
                </div>
            </div>
        </form>
    </Modal>
    
    <!-- Delete Confirmation Alert -->
    <Alert
        :show="showDeleteAlert"
        data-testid="eval-llm-delete-alert"
        title="Delete Eval LLM?"
        message="Are you sure you want to delete this Eval LLM? This action cannot be undone."
        type="danger"
        confirmText="Delete"
        cancelText="Cancel"
        @confirm="handleDeleteConfirm"
        @close="handleCloseDeleteAlert"
        :showCloseBtn="false"
    />

    <!-- Cropper Modal -->
    <div v-if="showCropper" class="cropper-modal" data-modal data-testid="eval-llm-cropper-modal">
        <div class="cropper-container">
            <div class="modal__header">
                <h2 class="modal__title">Crop Your Logo Image</h2>
                <button class="modal__close" @click="cancelCropping" data-testid="eval-llm-cropper-close-button">
                    <svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" fill="none" viewBox="0 0 24 24">
                        <path stroke="currentColor" stroke-linecap="round" stroke-linejoin="round" stroke-width="2"
                            d="M6 18L18 6M6 6l12 12" />
                    </svg>
                </button>
            </div>
            <div class="modal__body">
                <Cropper ref="cropperRef" :src="imageSource" @change="onCropperChange"
                    :stencil-component="CircleStencil" :stencil-props="{
                        aspectRatio: 1,
                        movable: true,
                        resizable: true,
                        handlers: {
                            eastNorth: true,
                            north: true,
                            westNorth: true,
                            west: true,
                            westSouth: true,
                            south: true,
                            eastSouth: true,
                            east: true
                        },
                        handlersClasses: {
                            default: 'handler-default'
                        },
                        lineWidth: 2
                    }" class="cropper" />
            </div>
            <div class="modal__footer">
                <button class="btn btn--secondary" @click="cancelCropping" data-testid="cancel-cropped-image-button">Cancel</button>
                <button class="btn btn--primary" @click="saveCroppedImage" data-testid="save-cropped-image-button">Save</button>
            </div>
        </div>
    </div>
</template>

<style scoped lang="postcss">
@keyframes slideIn {
    from {
        transform: translateY(20px);
        opacity: 0;
    }
    to {
        transform: translateY(0);
        opacity: 1;
    }
}

.animate-slideIn {
    animation: slideIn 0.3s cubic-bezier(0.34, 1.56, 0.64, 1);
}

/* Slide transition styles */
.slide-enter-active,
.slide-leave-active {
    transition: all 0.4s cubic-bezier(0.4, 0, 0.2, 1);
    overflow-x: hidden !important;
}

.slide-enter-from {
    transform: translateX(100%);
    opacity: 0;
}

.slide-leave-to {
    transform: translateX(-100%);
    opacity: 0;
}

.slide-enter-to,
.slide-leave-from {
    transform: translateX(0);
    opacity: 1;
}

.overflow-y-auto {
    scrollbar-width: thin;
    scrollbar-color: gray transparent;
}

.overflow-y-auto::-webkit-scrollbar {
    width: 6px;
}

.overflow-y-auto::-webkit-scrollbar-track {
    background: transparent;
}

.overflow-y-auto::-webkit-scrollbar-thumb {
    background-color: gray;
    border-radius: 3px;
    transition: background-color 0.3s ease;
}

.overflow-y-auto::-webkit-scrollbar-thumb:hover {
    background-color: #7C3AED;
}

.payment-method-card {
    @apply border-2 border-gray-600/50 rounded-xl px-4 pt-2 pb-4 text-center hover:bg-echoes-border-light transition-all duration-200 space-y-2 shadow-sm shadow-black transition delay-150 duration-300 ease-in-out hover:-translate-y-1 hover:scale-100;
}

.model-config-wrapper :deep(.text-input__textarea) {
    height: 250px;
    overflow-y: auto;
    resize: none;
    scrollbar-width: thin;
    scrollbar-color: gray transparent;
    transition: height 0.2s ease;
}

.model-config-wrapper :deep(.text-input__textarea)::-webkit-scrollbar {
    width: 6px;
}

.model-config-wrapper :deep(.text-input__textarea)::-webkit-scrollbar-track {
    background: transparent;
}

.model-config-wrapper :deep(.text-input__textarea)::-webkit-scrollbar-thumb {
    background-color: gray;
    border-radius: 3px;
    transition: background-color 0.3s ease;
}

.model-config-wrapper :deep(.text-input__textarea)::-webkit-scrollbar-thumb:hover {
    background-color: #7C3AED;
}

.backfill-wrapper {
    @apply w-full;
}

.cropper-modal {
    @apply fixed inset-0 z-[10000] flex items-center justify-center bg-black/50 p-2;
    backdrop-filter: blur(4px);
    animation: fadeIn 0.2s ease;
    isolation: isolate;
}

.cropper-container {
    @apply overflow-hidden bg-echoes-background-black border border-echoes-border-gray rounded-xl shadow-xl w-full max-h-[70vh] mt-10 flex flex-col;
    animation: slideInCropper 0.3s cubic-bezier(0.34, 1.56, 0.64, 1);
    position: relative;
    z-index: 1;
    max-width: 400px;
    @apply 2xl:max-w-lg;
}

.modal__header {
    @apply sticky top-0 z-10 flex items-center justify-between p-4 2xl:p-6 border-b border-echoes-border-gray bg-echoes-background-black;
}

.modal__title {
    @apply text-xl 2xl:text-2xl font-semibold text-white;
}

.modal__close {
    @apply text-white/80 hover:text-white p-1 rounded-full transition-colors;
}

.modal__body {
    @apply p-4 2xl:p-6 overflow-hidden;
    flex: 1;
    min-height: 0;
    display: flex;
    flex-direction: column;
}

.modal__footer {
    @apply sticky bottom-0 z-10 p-4 2xl:p-6 border-t border-echoes-border-gray flex items-center justify-end gap-3 bg-echoes-background-black;
}

.btn {
    @apply px-4 py-2 2xl:px-6 2xl:py-3 rounded-lg font-medium transition-colors;
}

.btn--secondary {
    @apply px-4 py-1.5 2xl:px-6 2xl:py-2.5 rounded-lg text-sm 2xl:text-base font-medium transition-colors dark:hover:border-echoes-primary/50 dark:hover:text-echoes-primary dark:bg-white/10 dark:text-white dark:border-white/20 hover:border-echoes-primary/30 hover:text-echoes-primary bg-white text-gray-700 border border-gray-200;
}

.btn--primary {
    @apply px-4 py-1.5 2xl:px-6 2xl:py-2.5 rounded-lg text-sm 2xl:text-base font-medium transition-colors bg-echoes-primary text-white hover:bg-echoes-primary/90;
}

.cropper {
    @apply bg-zinc-900;
    max-height: 300px;
    width: 100%;
    min-height: 250px;
}

.handler-default {
    @apply w-3 h-3 2xl:w-4 2xl:h-4 bg-echoes-primary border border-white rounded-full;
}

@keyframes fadeIn {
    from {
        opacity: 0;
    }

    to {
        opacity: 1;
    }
}

@keyframes slideInCropper {
    from {
        transform: translateY(20px);
        opacity: 0;
    }

    to {
        transform: translateY(0);
        opacity: 1;
    }
}

.logo-preview-circle {
    @apply w-[120px] h-[120px] rounded-full flex items-center justify-center relative overflow-hidden bg-echoes-background-black border-2 border-echoes-primary shadow-lg;
}

.logo-preview-image {
    @apply w-full h-full object-cover;
}
</style>
